import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../AuthContext';
import VerificationBox from './VerificationBox';

const Groups = () => {
  const { t } = useTranslation();
  const { 
    user, 
    getUserGroups, 
    getPublicGroups, 
    joinGroup, 
    joinPublicGroup, 
    leaveGroup, 
    deleteGroup 
  } = useAuth();
  
  const [userGroups, setUserGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publicGroupsLoading, setPublicGroupsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({ code: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [publicGroupsPagination, setPublicGroupsPagination] = useState(null);

  useEffect(() => {
    fetchUserGroups();
    fetchPublicGroups();
  }, []);

  const fetchUserGroups = async () => {
    setLoading(true);
    const result = await getUserGroups();
    if (result.success) {
      setUserGroups(result.groups);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const fetchPublicGroups = async (page = 1, search = searchTerm) => {
    setPublicGroupsLoading(true);
    const result = await getPublicGroups(page, 10, search);
    if (result.success) {
      setPublicGroups(result.groups);
      setPublicGroupsPagination(result.pagination);
    } else {
      setError(result.error);
    }
    setPublicGroupsLoading(false);
  };

  const handleJoinGroup = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    const result = await joinGroup(formData.code);
    if (result.success) {
      await fetchUserGroups();
      setFormData({ code: '' });
      setSuccess(t('groups.joinSuccess') || 'Successfully joined group!');
    } else {
      setError(result.error);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleLeaveGroup = async (groupToLeave) => {
    if (!groupToLeave || !window.confirm(t('groups.leaveConfirm') || 'Are you sure you want to leave this group?')) {
      return;
    }

    setError(null);
    setSuccess(null);
    
    const result = await leaveGroup(groupToLeave.id);
    if (result.success) {
      await fetchUserGroups(); // Refresh user groups
      setSuccess(t('groups.leaveSuccess') || 'Successfully left group!');
    } else {
      setError(result.error);
    }
  };

  if (loading) {
    return <div className="loading">Loading group information...</div>;
  }

  return (
    <div className="groups-container">
      {/* Rest der Groups Komponente */}
      <div className="groups-content">
        {/* Verifizierungsbox hier einbetten */}
        {user && !user.isVerified && (
          <VerificationBox 
            onVerificationSuccess={() => window.location.reload()} 
          />
        )}
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Join by Code Section */}
        <div className="content-box">
          <h3>{t('groups.joinByCode') || 'Join Group by Code'}</h3>
          <form onSubmit={handleJoinGroup}>
            <div className="form-group">
              <label htmlFor="joinCode">{t('groups.groupCode') || 'Group Code'}:</label>
              <input
                type="text"
                id="joinCode"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                required
                placeholder={t('groups.enterGroupCode') || 'Enter group code (e.g. ABC123)'}
                style={{ textTransform: 'uppercase' }}
              />
              <small className="form-hint">
                {t('groups.joinCodeHint') || 'Enter the 6-character code to join a private group'}
              </small>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {t('groups.joinGroup') || 'Join Group'}
              </button>
            </div>
          </form>
        </div>

        {/* My Groups Section */}
        {userGroups.length > 0 ? (
          <div className="content-box">
            <h3>{t('groups.myGroups') || 'My Groups'}</h3>
            {userGroups.map(group => (
              <div key={group.id} className="group-card">
                <div className="group-info">
                  <h4>{group.title}</h4>
                  <p>{group.description}</p>
                </div>
                <div className="group-actions">
                  <button 
                    className="btn btn-danger"
                    onClick={() => handleLeaveGroup(group)}
                  >
                    {t('groups.leaveGroup') || 'Leave Group'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="content-box">
            <p>{t('groups.noGroups') || 'You are not a member of any groups yet.'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Groups; 